import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  ArrowLeft, Loader2, FileText, Truck,
  Instagram, Plus, Trash2, Edit2, Link as LinkIcon, MapPin,
  MessageSquare, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { ShippingAddressDialog } from '../../components/ShippingAddressDialog';
import { MandatesPanel, MandateTag, mandatesForCreator } from '../../components/MandatesPanel';
import { CompletionReportPanel } from '../../components/bulk/CompletionReportPanel';
import { ResponseAnalyticsPanel } from '../../components/bulk/ResponseAnalyticsPanel';
import { MessageCreatorsDialog } from '../../components/bulk/MessageCreatorsDialog';
import { BotLaunchPanel } from '../../components/campaign/BotLaunchPanel';
import { SheetSyncControls } from '../../components/bulk/SheetSyncControls';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status meta — single source of truth. Order matters: drives the pipeline grid.
const STATUS_META = {
  invited:           { label: 'Invited',            color: '#71717a' },
  interested:        { label: 'Interested',         color: '#0055FF' },
  rejected:          { label: 'Rejected',           color: '#FF3B30' },
  no_response:       { label: 'No Response',        color: '#52525b' },
  approved_by_brand: { label: 'Approved by Brand',  color: '#FFB800' },
  product_shipped:   { label: 'Product Shipped',    color: '#A855F7' },
  product_delivered: { label: 'Product Delivered',  color: '#EC4899' },
  draft_submitted:   { label: 'Draft Submitted',    color: '#06B6D4' },
  revision_required: { label: 'Revision Required',  color: '#F97316' },
  approved:          { label: 'Approved',           color: '#84CC16' },
  posted:            { label: 'Posted',             color: '#22C55E' },
  completed:         { label: 'Completed',          color: '#00C972' },
};
const STATUS_LIST = Object.keys(STATUS_META);
const LOGISTICS_LIST = ['address_pending', 'address_received', 'product_shipped', 'product_delivered'];

// Product Dispatch Tracker — sits alongside logistics_status; never modifies address flow.
const DISPATCH_LIST = ['address_collected', 'product_dispatched', 'product_received', 'content_pending'];
const DISPATCH_META = {
  address_collected:  { label: 'Address Collected',  color: '#FF9500' }, // orange
  product_dispatched: { label: 'Product Dispatched', color: '#0055FF' }, // blue
  product_received:   { label: 'Product Received',   color: '#00C972' }, // green
  content_pending:    { label: 'Content Pending',    color: '#FFB800' }, // yellow
};

// Workflow gate sets — must match backend bulk_routes.py ACCEPTED_STATUSES + LOGISTICS_PROGRESSION.
// FE replicates these for UX (hide controls early); backend still enforces.
const ACCEPTED_STATUSES = new Set(['interested', 'approved_by_brand']);
const PRE_ACCEPTANCE_STATUSES = new Set(['invited', 'no_response', 'rejected']);
const LOGISTICS_DONE_STATUSES = new Set(['product_shipped', 'product_delivered', 'draft_submitted', 'revision_required', 'approved', 'posted', 'completed']);

// Allowed next-states — mirrors backend ALLOWED_STATUS_TRANSITIONS. Used to grey-out
// invalid options inside the per-row status <select> so the CM gets a smart, narrow menu.
const ALLOWED_STATUS_TRANSITIONS = {
  invited: new Set(['invited', 'interested', 'rejected', 'no_response']),
  no_response: new Set(['no_response', 'interested', 'rejected']),
  interested: new Set(['interested', 'approved_by_brand', 'rejected']),
  approved_by_brand: new Set(['approved_by_brand', 'product_shipped', 'rejected']),
  product_shipped: new Set(['product_shipped', 'product_delivered']),
  product_delivered: new Set(['product_delivered', 'draft_submitted']),
  draft_submitted: new Set(['draft_submitted', 'revision_required', 'approved']),
  revision_required: new Set(['revision_required', 'draft_submitted']),
  approved: new Set(['approved', 'posted']),
  posted: new Set(['posted', 'completed']),
  completed: new Set(['completed']),
  rejected: new Set(['rejected']),
};

// Quick-filter chips at the top of the Creators tab. Each one maps to a real filter
// expressed in terms of status (+ optional logistics_status).
const QUICK_FILTERS = [
  { key: 'all',             label: 'All',               filter: () => true },
  { key: 'invited',         label: 'Invited',           filter: (c) => c.status === 'invited' || c.status === 'no_response' },
  { key: 'interested',      label: 'Accepted',          filter: (c) => ACCEPTED_STATUSES.has(c.status) },
  { key: 'approved_brand',  label: 'Approved by Brand', filter: (c) => c.status === 'approved_by_brand' },
  { key: 'awaiting_addr',   label: 'Awaiting Address',  filter: (c) => ACCEPTED_STATUSES.has(c.status) && (c.logistics_status || 'address_pending') === 'address_pending' },
  { key: 'shipped',         label: 'Product Shipped',   filter: (c) => c.status === 'product_shipped' },
  { key: 'draft_pending',   label: 'Draft Pending',     filter: (c) => c.status === 'draft_submitted' || c.status === 'revision_required' },
  { key: 'approved_content',label: 'Approved Content',  filter: (c) => c.status === 'approved' },
  { key: 'posted',          label: 'Posted',            filter: (c) => c.status === 'posted' },
  { key: 'completed',       label: 'Completed',         filter: (c) => c.status === 'completed' },
];

const fmtInr = (n) => (n == null || n === '' ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);
const fmtNum = (n) => {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString();
};

export const BulkCampaignWorkspace = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canSeeBot = ['campaign_manager', 'manager', 'admin', 'account_executive'].includes(user?.role);

  const [campaign, setCampaign] = useState(null);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview'); // overview | creators | mandates | scripts

  // Brand-shared mandates (new system, additive)
  const [campaignMandates, setCampaignMandates] = useState([]);

  // Filter state for the Creators tab
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMandate, setFilterMandate] = useState('all');
  const [quickFilter, setQuickFilter] = useState('all');

  // Mandate modal
  const [mandateOpen, setMandateOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [editingMandate, setEditingMandate] = useState(null);
  const [mForm, setMForm] = useState({ title: '', script: '', talking_points: '', dos: '', donts: '', reference_link: '' });

  // Assign mandate to selected creators
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [assignToMandate, setAssignToMandate] = useState('');

  // Shipping-address dialog (NEW — additive, does not change pipeline logic)
  const [addressRow, setAddressRow] = useState(null);

  // Status quick-change dropdown selection — keyed by row id
  const reload = async () => {
    const [{ data: c }, { data: rows }] = await Promise.all([
      axios.get(`${API_URL}/api/bulk/campaigns/${id}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/bulk/campaigns/${id}/creators`, { withCredentials: true }),
    ]);
    setCampaign(c);
    setCreators(rows);
    // Load brand-shared mandates (silent on failure — non-blocking).
    try {
      const { data: ms } = await axios.get(
        `${API_URL}/api/brand/campaigns/${id}/mandates`,
        { withCredentials: true },
      );
      setCampaignMandates(ms || []);
    } catch (_) { /* CM may not be assigned → ignore */ }
  };

  const reloadMandatesOnly = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/brand/campaigns/${id}/mandates`,
        { withCredentials: true },
      );
      setCampaignMandates(data || []);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        await reload();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load campaign');
        navigate('/bulk/campaigns');
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const mandates = campaign?.pipeline?.mandates || [];
  const counts = campaign?.pipeline?.counts || {};
  const completionPct = campaign?.pipeline?.completion_pct ?? 0;

  const filteredCreators = useMemo(() => {
    const qf = QUICK_FILTERS.find((q) => q.key === quickFilter) || QUICK_FILTERS[0];
    return creators.filter((c) => {
      if (!qf.filter(c)) return false;
      if (filterStatus !== 'all' && c.status !== filterStatus) return false;
      if (filterMandate === 'unassigned' && c.mandate_id) return false;
      if (filterMandate !== 'all' && filterMandate !== 'unassigned' && c.mandate_id !== filterMandate) return false;
      return true;
    });
  }, [creators, filterStatus, filterMandate, quickFilter]);

  // ---- Actions ----
  const updateStatus = async (rowId, status) => {
    try {
      await axios.patch(`${API_URL}/api/bulk/creators/${rowId}/status`, { status }, { withCredentials: true });
      await reload();
      toast.success(`Status → ${STATUS_META[status]?.label || status}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const updateLogistics = async (rowId, logistics_status) => {
    try {
      await axios.patch(`${API_URL}/api/bulk/creators/${rowId}/logistics`, { logistics_status }, { withCredentials: true });
      await reload();
      toast.success(`Logistics → ${logistics_status.replace('_', ' ')}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const updateDispatch = async (rowId, dispatch_status) => {
    try {
      await axios.patch(`${API_URL}/api/campaigns/${rowId}/dispatch-status`, { dispatch_status }, { withCredentials: true });
      await reload();
      toast.success(`Dispatch → ${dispatch_status.replace(/_/g, ' ')}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update dispatch');
    }
  };

  const openCreateMandate = () => {
    setEditingMandate(null);
    setMForm({ title: '', script: '', talking_points: '', dos: '', donts: '', reference_link: '' });
    setMandateOpen(true);
  };
  const openEditMandate = (m) => {
    setEditingMandate(m);
    setMForm({
      title: m.title,
      script: m.script,
      talking_points: (m.talking_points || []).join('\n'),
      dos: (m.dos || []).join('\n'),
      donts: (m.donts || []).join('\n'),
      reference_link: m.reference_link || '',
    });
    setMandateOpen(true);
  };
  const saveMandate = async (e) => {
    e.preventDefault();
    const payload = {
      title: mForm.title.trim(),
      script: mForm.script.trim(),
      talking_points: mForm.talking_points.split('\n').map((s) => s.trim()).filter(Boolean),
      dos: mForm.dos.split('\n').map((s) => s.trim()).filter(Boolean),
      donts: mForm.donts.split('\n').map((s) => s.trim()).filter(Boolean),
      reference_link: mForm.reference_link.trim() || null,
    };
    if (!payload.title || !payload.script) {
      toast.error('Title and script are required'); return;
    }
    try {
      if (editingMandate) {
        await axios.patch(`${API_URL}/api/bulk/mandates/${editingMandate.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/bulk/campaigns/${id}/mandates`, payload, { withCredentials: true });
      }
      setMandateOpen(false);
      await reload();
      toast.success(editingMandate ? 'Mandate updated' : 'Mandate created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };
  const deleteMandate = async (mandateId) => {
    if (!window.confirm('Delete this mandate? Creators currently bound to it will be unassigned.')) return;
    try {
      await axios.delete(`${API_URL}/api/bulk/mandates/${mandateId}`, { withCredentials: true });
      await reload();
      toast.success('Deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleRowSelect = (rowId) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
      return next;
    });
  };

  const assignSelectedToMandate = async () => {
    if (!assignToMandate || selectedRowIds.size === 0) {
      toast.error('Pick a mandate and at least one creator'); return;
    }
    try {
      const { data } = await axios.post(
        `${API_URL}/api/bulk/mandates/${assignToMandate}/assign`,
        { creator_ids: Array.from(selectedRowIds) },
        { withCredentials: true }
      );
      toast.success(`Assigned ${data.assigned} creator${data.assigned === 1 ? '' : 's'}`);
      setSelectedRowIds(new Set());
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assign failed');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center bg-white dark:bg-[#0A0B0D]">
          <Loader2 className="animate-spin text-[#FF451A]" size={32} />
        </div>
      </div>
    );
  }
  if (!campaign) return null;

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="bulk-workspace-page">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/bulk/campaigns')}
            className="text-sm text-[#6B6B6B] dark:text-zinc-400 hover:text-white flex items-center gap-1 mb-6"
            data-testid="bulk-back-button"
          >
            <ArrowLeft size={14} /> All bulk campaigns
          </button>

          <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
            <div className="min-w-0">
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-1" data-testid="bulk-workspace-title">
                {campaign.campaign_name}
              </h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400">
                {campaign.brand_name} · <span className="font-mono text-[#FF451A]">{fmtInr(campaign.fixed_cost_per_creator)}</span> per creator · {(campaign.platforms || []).join(', ')}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <SheetSyncControls campaignId={id} role={user?.role} />
              <button
                onClick={() => setMessageDialogOpen(true)}
                disabled={creators.length === 0}
                className="bg-[#25D366] hover:bg-[#20B85A] text-[#0F0F0F] dark:text-white rounded-full px-4 py-2 text-xs font-medium disabled:opacity-40 inline-flex items-center gap-1.5 transition-colors"
                data-testid="message-creators-btn"
                title="Send a WhatsApp template to selected creators"
              >
                <MessageSquare size={13} /> Message Creators
              </button>
            </div>
          </div>

          {/* Dashboard tiles — match Part 1 §7 + Part 3 §3 spec vocabulary */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6" data-testid="bulk-dashboard-tiles">
            <Tile label="Invited"           value={campaign.pipeline.invited ?? 0}             color="#71717a" testid="tile-invited" />
            <Tile label="Accepted"          value={campaign.pipeline.accepted ?? 0}            color="#0055FF" testid="tile-accepted" />
            <Tile label="Approved"          value={(counts.approved_by_brand || 0) + (counts.approved || 0)} color="#FFB800" testid="tile-approved" />
            <Tile label="Awaiting Address"  value={campaign.pipeline.awaiting_address ?? 0}    color="#A855F7" testid="tile-awaiting-address" />
            <Tile label="Delivered"         value={campaign.pipeline.products_delivered}       color="#EC4899" testid="tile-delivered" />
            <Tile label="Drafts Pending"    value={campaign.pipeline.drafts_pending}           color="#06B6D4" testid="tile-drafts" />
            <Tile label="Posted"            value={campaign.pipeline.posted_content}           color="#22C55E" testid="tile-posted" />
            <Tile label="Completion"        value={`${completionPct}%`}                        color="#00C972" testid="tile-completion" />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 border-b border-black/[0.08] dark:border-white/5" data-testid="bulk-tabs">
            {[
              { k: 'overview',  label: 'Pipeline' },
              { k: 'creators',  label: `Creators (${creators.length})` },
              { k: 'mandates',  label: `Mandates (${campaignMandates.length})` },
              { k: 'scripts',   label: `Scripts (${mandates.length})` },
              { k: 'responses', label: 'Response Analytics' },
              { k: 'report',    label: 'Completion Report' },
              ...(canSeeBot ? [{ k: 'bot', label: 'Bot Launch' }] : []),
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  tab === t.k ? 'border-[#FF451A] text-[#0F0F0F] dark:text-white' : 'border-transparent text-[#6B6B6B] dark:text-zinc-400 hover:text-white'
                }`}
                data-testid={`tab-${t.k}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'overview' && (
            <div className="space-y-6" data-testid="tab-content-overview">
              <Section title="Creator pipeline">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="pipeline-grid">
                  {STATUS_LIST.map((s) => (
                    <button
                      key={s}
                      onClick={() => { setFilterStatus(s); setTab('creators'); }}
                      className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-3 text-left hover:border-white/15 transition-colors"
                      data-testid={`pipeline-cell-${s}`}
                    >
                      <div className="text-[10px] uppercase tracking-wider font-mono mb-1" style={{ color: STATUS_META[s].color }}>
                        {STATUS_META[s].label}
                      </div>
                      <div className="text-xl font-bold text-[#0F0F0F] dark:text-white font-mono">{counts[s] || 0}</div>
                    </button>
                  ))}
                </div>
              </Section>

              <Section title="Logistics breakdown">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="logistics-grid">
                  {LOGISTICS_LIST.map((s) => (
                    <div key={s} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-3">
                      <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1 font-mono inline-flex items-center gap-1">
                        <Truck size={10} /> {s.replace('_', ' ')}
                      </div>
                      <div className="text-xl font-bold text-[#0F0F0F] dark:text-white font-mono" data-testid={`logistics-${s}`}>
                        {campaign.pipeline.logistics?.[s] || 0}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Mandate split">
                {mandates.length === 0 ? (
                  <div className="text-xs text-[#6B6B6B] dark:text-zinc-500">No mandates yet. Switch to the Mandates tab to add Script A/B/C…</div>
                ) : (
                  <div className="space-y-2" data-testid="mandate-split-list">
                    {mandates.map((m) => (
                      <div key={m.id} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-3 flex items-center justify-between">
                        <div className="font-semibold text-[#0F0F0F] dark:text-white text-sm">{m.title}</div>
                        <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 font-mono">{m.assigned_creator_count || 0} creators</div>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            </div>
          )}

          {tab === 'creators' && (
            <div className="space-y-4" data-testid="tab-content-creators">
              {/* Quick filter chips — one click jumps to a stage */}
              <div className="flex flex-wrap gap-1.5" data-testid="quick-filter-chips">
                {QUICK_FILTERS.map((qf) => {
                  const active = quickFilter === qf.key;
                  const matchCount = creators.filter(qf.filter).length;
                  return (
                    <button
                      key={qf.key}
                      onClick={() => setQuickFilter(qf.key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors inline-flex items-center gap-1.5 ${
                        active
                          ? 'bg-[#FF451A] text-[#0F0F0F] dark:text-white border border-[#FF451A]'
                          : 'bg-white/5 text-zinc-300 border border-black/[0.10] dark:border-white/10 hover:bg-white/10'
                      }`}
                      data-testid={`quick-filter-${qf.key}`}
                    >
                      {qf.label}
                      <span className={`font-mono text-[10px] ${active ? 'text-white/80' : 'text-[#6B6B6B] dark:text-zinc-500'}`}>{matchCount}</span>
                    </button>
                  );
                })}
              </div>

              {/* Filters + bulk assign */}
              <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-4 flex flex-wrap gap-3 items-end" data-testid="creator-filters">
                <div>
                  <label className="text-[10px] uppercase tracking-wider block mb-1 font-mono text-[#0F0F0F] dark:text-white">Status</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="rounded-lg text-sm px-3 py-1.5 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                    data-testid="filter-status"
                  >
                    <option value="all">All</option>
                    {STATUS_LIST.map((s) => (
                      <option key={s} value={s}>{STATUS_META[s].label} ({counts[s] || 0})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider block mb-1 font-mono text-[#0F0F0F] dark:text-white">Mandate</label>
                  <select
                    value={filterMandate}
                    onChange={(e) => setFilterMandate(e.target.value)}
                    className="rounded-lg text-sm px-3 py-1.5 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                    data-testid="filter-mandate"
                  >
                    <option value="all">All</option>
                    <option value="unassigned">Unassigned</option>
                    {mandates.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
                  </select>
                </div>
                {selectedRowIds.size > 0 && (
                  <div className="flex items-end gap-2 ml-auto" data-testid="bulk-assign-bar">
                    <div className="text-xs text-[#FF451A] font-mono font-semibold pb-2">
                      {selectedRowIds.size} selected
                    </div>
                    <select
                      value={assignToMandate}
                      onChange={(e) => setAssignToMandate(e.target.value)}
                      className="rounded-lg text-sm px-3 py-1.5 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                      data-testid="bulk-assign-mandate-select"
                    >
                      <option value="">Assign to mandate…</option>
                      {mandates.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
                    </select>
                    <Button onClick={assignSelectedToMandate} disabled={!assignToMandate} className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-lg px-4 py-1.5 text-sm font-medium hover:bg-[#E63E17] disabled:opacity-40" data-testid="bulk-assign-go">
                      Assign
                    </Button>
                  </div>
                )}
              </div>

              {/* Creator rows */}
              {filteredCreators.length === 0 ? (
                <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-12 text-center text-sm text-[#6B6B6B] dark:text-zinc-500" data-testid="creators-empty-state">
                  No creators match the current filter.
                </div>
              ) : (
                <div className="space-y-2" data-testid="creators-rows">
                  {filteredCreators.map((c) => {
                    const meta = STATUS_META[c.status] || STATUS_META.invited;
                    const mandate = mandates.find((m) => m.id === c.mandate_id);
                    const isSelected = selectedRowIds.has(c.id);
                    const openDetail = () => navigate(`/bulk/campaigns/${id}/creators/${c.id}`);
                    return (
                      <div
                        key={c.id}
                        className={`bg-white dark:bg-[#0A0B0D] rounded-xl p-3 border transition-colors ${
                          isSelected ? 'border-[#FF451A] bg-[#FF451A]/[0.04]' : 'border-black/[0.08] dark:border-white/5 hover:border-white/10'
                        }`}
                        data-testid="creator-row"
                      >
                        <div className="flex items-center gap-3 flex-wrap">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleRowSelect(c.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 accent-[#FF451A] shrink-0"
                            data-testid="creator-checkbox"
                          />
                          <button
                            type="button"
                            onClick={openDetail}
                            className="flex items-center gap-3 flex-1 min-w-0 text-left group"
                            data-testid="creator-row-open"
                          >
                            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-white text-sm font-bold shrink-0">
                              {c.creator_name?.[0]?.toUpperCase() || 'C'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-[#0F0F0F] dark:text-white truncate group-hover:text-[#FF451A] transition-colors">{c.creator_name}</div>
                              {c.instagram_handle && (
                                <span className="text-[11px] text-[#6B6B6B] dark:text-zinc-500 inline-flex items-center gap-1">
                                  <Instagram size={10} /> @{c.instagram_handle} · {fmtNum(c.instagram_followers)}
                                </span>
                              )}
                              {/* Brand-shared mandate tags (NEW) — additive, doesn't replace existing script chip */}
                              {(() => {
                                const rowMandates = mandatesForCreator(campaignMandates, c.id);
                                return rowMandates.length > 0 ? (
                                  <div className="flex flex-wrap gap-1 mt-1" data-testid="bulk-row-mandate-tags">
                                    {rowMandates.map((m) => <MandateTag key={m.mandate_id} mandate={m} />)}
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          </button>
                          {mandate && (
                            <span className="px-2 py-0.5 text-[10px] rounded-full bg-[#0055FF]/10 text-[#0055FF] border border-[#0055FF]/20 font-mono font-medium shrink-0">
                              {mandate.title}
                            </span>
                          )}
                          <select
                            value={c.status}
                            onChange={(e) => updateStatus(c.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] rounded-lg text-xs text-[#0F0F0F] dark:text-white px-2 py-1 font-medium shrink-0"
                            style={{ color: meta.color, borderColor: `${meta.color}40` }}
                            data-testid="creator-status-select"
                          >
                            {STATUS_LIST.filter((s) => (ALLOWED_STATUS_TRANSITIONS[c.status] || new Set([c.status])).has(s)).map((s) => (
                              <option key={s} value={s} style={{ color: '#fff', backgroundColor: '#0F0F0F' }}>{STATUS_META[s].label}</option>
                            ))}
                          </select>
                          {/* Logistics only meaningful after acceptance — hide for invited/declined */}
                          {(ACCEPTED_STATUSES.has(c.status) || LOGISTICS_DONE_STATUSES.has(c.status)) && (
                            <select
                              value={c.logistics_status || 'address_pending'}
                              onChange={(e) => updateLogistics(c.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="rounded-lg text-xs px-2 py-1 font-medium shrink-0 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                              data-testid="creator-logistics-select"
                            >
                              {LOGISTICS_LIST.map((s, idx) => {
                                const curIdx = LOGISTICS_LIST.indexOf(c.logistics_status || 'address_pending');
                                const disabled = idx !== curIdx && idx !== curIdx + 1;
                                return (
                                  <option key={s} value={s} disabled={disabled} style={{ backgroundColor: '#0F0F0F' }}>
                                    {s.replace('_', ' ')}
                                  </option>
                                );
                              })}
                            </select>
                          )}

                          {/* Product Dispatch Tracker — separate from address collection & logistics.
                              Only rendered once shipping_address exists (rule: gated on address collected). */}
                          {(c.shipping_address && typeof c.shipping_address === 'object' && c.shipping_address.address_line_1) && (
                            (() => {
                              const cur = c.dispatch_status || 'address_collected';
                              const meta = DISPATCH_META[cur];
                              return (
                                <select
                                  value={cur}
                                  onChange={(e) => updateDispatch(c.id, e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="border rounded-lg text-xs px-2 py-1 font-mono shrink-0"
                                  style={{
                                    color: meta.color,
                                    borderColor: `${meta.color}60`,
                                    backgroundColor: `${meta.color}12`,
                                  }}
                                  data-testid="creator-dispatch-select"
                                  title="Product dispatch status"
                                >
                                  {DISPATCH_LIST.map((s) => (
                                    <option key={s} value={s} style={{ backgroundColor: '#0F0F0F', color: '#fff' }}>
                                      {DISPATCH_META[s].label}
                                    </option>
                                  ))}
                                </select>
                              );
                            })()
                          )}
                          {/* Structured shipping-address collection — chip or button.
                              Only shown once creator has accepted; pre-acceptance the address has no use. */}
                          {(ACCEPTED_STATUSES.has(c.status) || LOGISTICS_DONE_STATUSES.has(c.status)) && (
                            (c.shipping_address && typeof c.shipping_address === 'object' && c.shipping_address.address_line_1) ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); setAddressRow(c); }}
                                className="px-2 py-1 rounded-lg text-[10px] font-mono uppercase tracking-wider bg-[#00C972]/10 text-[#00C972] border border-[#00C972]/30 hover:bg-[#00C972]/20 transition-colors shrink-0 inline-flex items-center gap-1"
                                data-testid={`creator-address-collected-${c.id}`}
                                title="Edit shipping address"
                              >
                                <MapPin size={10} /> Address Collected
                              </button>
                            ) : (
                              <button
                                onClick={(e) => { e.stopPropagation(); setAddressRow(c); }}
                                className="px-2 py-1 rounded-lg text-[10px] font-mono uppercase tracking-wider bg-[#FF451A]/10 text-[#FF451A] border border-[#FF451A]/30 hover:bg-[#FF451A]/20 transition-colors shrink-0 inline-flex items-center gap-1"
                                data-testid={`creator-add-address-${c.id}`}
                              >
                                <Plus size={10} /> Add Address
                              </button>
                            )
                          )}
                          <button
                            type="button"
                            onClick={openDetail}
                            className="text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF451A] transition-colors shrink-0 p-1"
                            data-testid="creator-row-detail-chevron"
                            title="Open creator workspace"
                          >
                            <ChevronRight size={16} />
                          </button>
                        </div>
                        {PRE_ACCEPTANCE_STATUSES.has(c.status) && (
                          <div className="mt-2 text-[11px] text-[#6B6B6B] dark:text-zinc-500 italic" data-testid="creator-pre-acceptance-hint">
                            {c.status === 'rejected' ? 'Creator declined the invite — no further action.' : 'Awaiting creator acceptance. Use the status dropdown to mark interest manually if they confirmed offline.'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === 'mandates' && (
            <div data-testid="tab-content-mandates" className="space-y-4">
              <MandatesPanel
                campaignId={id}
                mandates={campaignMandates}
                onChange={reloadMandatesOnly}
                roster={creators.map((c) => ({ id: c.id, name: c.creator_name || '—' }))}
              />
            </div>
          )}

          {tab === 'responses' && (
            <div data-testid="tab-content-responses">
              <ResponseAnalyticsPanel campaignId={id} />
            </div>
          )}

          {tab === 'report' && (
            <div data-testid="tab-content-report">
              <CompletionReportPanel
                campaignId={id}
                creators={creators}
                campaignStatus={campaign?.status}
                onCampaignCompleted={reload}
              />
            </div>
          )}

          {tab === 'bot' && canSeeBot && (
            <div data-testid="tab-content-bot">
              <BotLaunchPanel campaignId={id} creators={creators} onReload={reload} />
            </div>
          )}

          {tab === 'scripts' && (
            <div data-testid="tab-content-scripts">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">Pre-approved scripts. Assign creators in the Creators tab.</p>
                <Button onClick={openCreateMandate} className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-[#E63E17] flex items-center gap-1.5" data-testid="add-mandate-button">
                  <Plus size={14} /> Add script
                </Button>
              </div>
              {mandates.length === 0 ? (
                <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-12 text-center" data-testid="mandates-empty-state">
                  <FileText className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" size={40} />
                  <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">No scripts yet. Add Script A to get started.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="mandates-grid">
                  {mandates.map((m) => (
                    <div key={m.id} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5" data-testid="mandate-card">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="text-base font-semibold text-[#0F0F0F] dark:text-white">{m.title}</div>
                          <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-500 font-mono">{m.assigned_creator_count || 0} creators assigned</div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button onClick={() => openEditMandate(m)} className="p-1.5 text-[#6B6B6B] dark:text-zinc-400 hover:text-white transition-colors" data-testid="mandate-edit-button"><Edit2 size={13} /></button>
                          <button onClick={() => deleteMandate(m.id)} className="p-1.5 text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF3B30] transition-colors" data-testid="mandate-delete-button"><Trash2 size={13} /></button>
                        </div>
                      </div>
                      <div className="text-sm text-zinc-300 whitespace-pre-wrap mb-3 line-clamp-4">{m.script}</div>
                      {(m.talking_points || []).length > 0 && (
                        <MandateBlock label="Talking points" items={m.talking_points} color="#0055FF" />
                      )}
                      {(m.dos || []).length > 0 && (
                        <MandateBlock label="Do's" items={m.dos} color="#00C972" />
                      )}
                      {(m.donts || []).length > 0 && (
                        <MandateBlock label="Don'ts" items={m.donts} color="#FF3B30" />
                      )}
                      {m.reference_link && (
                        <a href={m.reference_link} target="_blank" rel="noreferrer" className="text-xs text-[#FF451A] hover:underline inline-flex items-center gap-1 mt-2">
                          <LinkIcon size={10} /> Reference
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mandate modal */}
      <Dialog open={mandateOpen} onOpenChange={setMandateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingMandate ? 'Edit mandate' : 'New mandate'}</DialogTitle>
            <DialogDescription className="text-sm text-[#6B6B6B] dark:text-zinc-400">
              Pre-approved by the brand. Creators assigned to this mandate use these talking points.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveMandate} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <Field label="Title">
              <Input value={mForm.title} onChange={(e) => setMForm({ ...mForm, title: e.target.value })}
                placeholder="Script A" className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="mandate-title-input" />
            </Field>
            <Field label="Script">
              <textarea value={mForm.script} onChange={(e) => setMForm({ ...mForm, script: e.target.value })}
                rows={5} placeholder="Hi! Today I'm sharing..." className="w-full rounded-xl text-sm p-3 outline-none focus:border-[#FF451A]/40 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="mandate-script-input" />
            </Field>
            <Field label="Talking points (one per line)">
              <textarea value={mForm.talking_points} onChange={(e) => setMForm({ ...mForm, talking_points: e.target.value })}
                rows={3} className="w-full rounded-xl text-sm p-3 outline-none focus:border-[#FF451A]/40 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="mandate-points-input" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Do's (one per line)">
                <textarea value={mForm.dos} onChange={(e) => setMForm({ ...mForm, dos: e.target.value })}
                  rows={3} className="w-full rounded-xl text-sm p-3 outline-none focus:border-[#00C972]/40 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="mandate-dos-input" />
              </Field>
              <Field label="Don'ts (one per line)">
                <textarea value={mForm.donts} onChange={(e) => setMForm({ ...mForm, donts: e.target.value })}
                  rows={3} className="w-full rounded-xl text-sm p-3 outline-none focus:border-[#FF3B30]/40 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="mandate-donts-input" />
              </Field>
            </div>
            <Field label="Reference link (optional)">
              <Input value={mForm.reference_link} onChange={(e) => setMForm({ ...mForm, reference_link: e.target.value })}
                placeholder="https://..." className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="mandate-ref-input" />
            </Field>
            <div className="flex gap-2 pt-1">
              <Button type="submit" className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 hover:bg-[#E63E17] flex-1" data-testid="mandate-submit">
                {editingMandate ? 'Update' : 'Create'}
              </Button>
              <Button type="button" onClick={() => setMandateOpen(false)} className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-5">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Shipping-address dialog (NEW) — additive, isolated. */}
      <ShippingAddressDialog
        row={addressRow}
        onClose={() => setAddressRow(null)}
        onSaved={() => { setAddressRow(null); reload(); }}
      />

      {/* WhatsApp Bulk Message Modal — sits in main component scope */}
      <MessageCreatorsDialog
        open={messageDialogOpen}
        onOpenChange={setMessageDialogOpen}
        campaignId={id}
        campaignName={campaign?.campaign_name}
        brandName={campaign?.brand_name}
        creators={creators}
      />
    </div>
  );
};

// --- Sub-components ----------------------------------------------------------
const Tile = ({ label, value, color, testid }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1 font-mono">{label}</div>
    <div className="text-xl font-bold text-[#0F0F0F] dark:text-white font-mono" style={{ color }}>{value}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5">
    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-4 font-mono">{title}</div>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1.5 font-mono text-[#0F0F0F] dark:text-white">{label}</label>
    {children}
  </div>
);

const MandateBlock = ({ label, items, color }) => (
  <div className="mb-2">
    <div className="text-[10px] uppercase tracking-wider mb-1 font-mono" style={{ color }}>{label}</div>
    <ul className="text-xs text-[#6B6B6B] dark:text-zinc-400 space-y-0.5 list-disc list-inside">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  </div>
);
